import polarisWrapper from '@amzn/awsui-components-react/test-utils/dom';
/**
 * Attempts to locate and bind values to components in a form using the
 * awsui test utils package.  Attempting to use standard enzyme does not
 * result in change events firing unless you reach down into the nested
 * internal components, which is discouraged.  The test utils act as an
 * abstraction layer, giving the team the freedom to change the internal
 * components going forward without fear of breaking tests that use them.
 *
 * For more information, see
 * https://polaris.corp.amazon.com/getting_started/development/testing/introduction/
 *
 */
/**
 * Binds a value to an AWSUI Input.
 *
 * @param componentId the id of the input to locate
 * @param elem outer element that contains all of the form content
 * @param value the value to enter into the input
 */
const bindInput = componentId => (elem, value) => {
    const pWrapper = polarisWrapper(elem);
    let inputWrapper;
    try {
        inputWrapper = pWrapper.findInput(`[id="${componentId}"]`);
    }
    catch (error) {
        throw new Error(`Could not locate Input with id ${componentId} within elem.`);
    }
    inputWrapper.setInputValue(value);
};
/**
 * Binds a value to an AWSUI Select Box.
 *
 * @param componentId the id of the select box to locate
 * @param selectableIds function to convert ids to select box index
 * @param elem outer element that contains all of the form content
 * @param selectedId The id to select
 */
const bindSelect = (componentId, selectableIds) => (elem, selectedId) => {
    const pWrapper = polarisWrapper(elem);
    let selectWrapper;
    try {
        selectWrapper = pWrapper.findSelect(`[id="${componentId}"]`);
    }
    catch (error) {
        throw new Error(`Could not locate Select with id ${componentId} within elem.`);
    }
    selectWrapper.openDropdown();
    const selectIndex = selectableIds.indexOf(selectedId);
    if (selectIndex === -1) {
        throw new Error(`id '${selectedId}' not found in list of selectableIds within '${componentId}'. Ensure withSelect called with correct values.`);
    }
    // selectOption is 1-based indexing
    selectWrapper.selectOption(1 + selectIndex);
};
/**
 * Binds a value to an AWSUI Checkbox.
 *
 * @param componentId the id of the checkbox to locate
 * @param elem outer element that contains all of the form content
 * @param value the value to enter into the input
 */
const bindCheckbox = componentId => (elem, 
// @ts-ignore
value // eslint-disable-line no-unused-vars
) => {
    const pWrapper = polarisWrapper(elem);
    let cbWrapper;
    try {
        cbWrapper = pWrapper.findCheckbox(`[id="${componentId}"]`);
    }
    catch (error) {
        throw new Error(`Could not locate Checkbox with id ${componentId} within elem.`);
    }
    cbWrapper.getElement().click();
};
class Builder {
    constructor() {
        this.fields = {};
    }
    /**
     * Registers an Input field in the form.
     * @param id the id attribute of the input to register
     * @param defaultValue the default value when resetting the form to empty state.
     * @returns {Builder} new builder with input registered.
     */
    withInput(id, defaultValue) {
        this.fields[id] = {
            bindFunc: () => bindInput(id),
            defaultValue,
        };
        return this;
    }
    /**
     * Registers a Select field in the form.
     * @param id the id attribute of the select to register
     * @param selectableIds list of possible IDs in the drop down.
     * @param defaultValue the default value (ID) when resetting the form to empty state.
     * @returns {Builder} new builder with select registered.
     */
    withSelect(id, selectableIds, defaultValue) {
        this.fields[id] = {
            bindFunc: bindSelect(id, selectableIds),
            defaultValue: defaultValue,
        };
        return this;
    }
    /**
     * Registers a Checkbox field in the form.
     * @param id the id attribute of the checkbox to register
     * @param defaultValue the default state (true or false)
     * @returns {Builder} new builder with checkbox registered.
     */
    withCheckbox(id, defaultValue = false) {
        this.fields[id] = {
            bindFunc: () => bindCheckbox(id),
            defaultValue,
        };
        return this;
    }
    build() {
        return new PolarisFormBinder(this);
    }
}
class PolarisFormBinder {
    constructor(build) {
        /**
         * Resets the form to default values.
         * @param elem outer element that contains all of the form content
         */
        this.resetForm = elem => {
            Object.keys(this.fields).forEach(id => {
                this.fields[id].bindFunc(elem, this.fields[id].defaultValue);
            });
        };
        /**
         * Bind a value to a specific field of the form.
         *
         * @param elem outer element that contains all of the form content
         * @param id the id of the field to bind
         * @param value the value to bind
         */
        this.bindValue = (elem, id, value) => {
            if (!(id in this.fields)) {
                throw new Error(`Form element id '${id}' is not registered.  Include it in the builder.`);
            }
            this.fields[id].bindFunc(elem, value);
        };
        this.fields = build.fields;
    }
    // @ts-ignore
    static get Builder() {
        return Builder;
    }
}
export default PolarisFormBinder;
//# sourceMappingURL=PolarisFormBinder.js.map